import React, { useContext, createContext, useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { styled } from '@linaria/react';
import useUserNotifications from 'hooks/useUserNotifications.js';
import useNotifications from 'hooks/useNotifications.js';
import * as ROUTES from 'constants/routes.js';
import { buildRoute } from 'constants/routes.js';
import { useLocalization } from './LocalizationContext.jsx';
import { ONE_TO_MANY_PRODUCT, ON_DEMAND_PRODUCT, PRODUCT_TO_INTERNAL_REF } from 'constants/products.js';
import { Link, COLORS } from '@livecommerce/design-system';

const StyledNotificationLink = styled(Link)`
  color: ${COLORS.white};
`;

const KNOWN_TOAST_NOTIFICATION_SOURCES = [
  'dubbing-complete',
  'vod-automatic-moderation-video-flagged',
  'product-knowledge-ready',
];
const KNOWN_BANNER_NOTIFICATION_SOURCES = [
  'platform-trigger-free-trial-vod-15-days-expire-nudge',
  'platform-trigger-free-trial-vod-5-days-expire-nudge',
  'platform-trigger-free-trial-vod-1-days-expire-nudge',
];

const defaultValue = {};
export const UserNotificationsContext = createContext(defaultValue);

export const UserNotificationsContextProvider = (props) => {
  const history = useHistory();
  const { t } = useLocalization();

  const [mostRecentToastNotification, setMostRecentToastNotification] = useState(null);
  const [mostRecentBannerNotification, setMostRecentBannerNotification] = useState(null);
  // TODO: Consilidate these into a single useUserNotifications hook, but first we need
  // the request to support multiple types in one request, currently we only support one type per request.
  // Instead, to avoid doing 2 requests per 30s which we do by default unless configured otherwise,
  // lets make the polling interval to 60s for now, netting in the same amount of polling.
  const {
    data: toasts = [],
    markNotificationAsSeen: markToastAsSeen,
  } = useUserNotifications({ type: 'bamhub-toast', pollIntervalMs: 60000 });
  const {
    data: banners = [],
    markNotificationAsSeen: markBannerAsSeen,
  } = useUserNotifications({ type: 'bamhub-banner', pollIntervalMs: 60000 });

  const { handleFooterNotification, clearFooterNotification } = useNotifications();

  useEffect(() => {
    if (mostRecentToastNotification) {
      const { id, data = {}, product, orgId, source } = mostRecentToastNotification || {};
      const { showId, videoId, productId, feedId } = data;

      const notificationPresentationDetails = {};
      // Map appropriate link based on product and showId or videoId
      if (product === PRODUCT_TO_INTERNAL_REF[ONE_TO_MANY_PRODUCT] && showId) {
        const showLink = showId && buildRoute(ROUTES.ORG_SHOW, { orgId, showId });
        notificationPresentationDetails.link = showLink;
        notificationPresentationDetails.shouldShowAction = !window.location?.href?.includes(showLink);
      }
      if (product === PRODUCT_TO_INTERNAL_REF[ON_DEMAND_PRODUCT] && videoId) {
        const videoLink = videoId && buildRoute(ROUTES.ORG_ON_DEMAND_VIDEO_SETUP, { orgId, videoId });
        notificationPresentationDetails.link = videoLink;
        notificationPresentationDetails.shouldShowAction = !window.location?.href?.includes(videoLink);
      }
      // Built from raw ids rather than reused from the backend's own
      // data.ctaUrl: that one is origin-qualified for the email link, and
      // history.push below needs a router-relative path instead.
      // `selectedProduct` names the Live/VOD context segment every org-scoped
      // route carries — ONE_TO_MANY_PRODUCT ('live') is where the product
      // catalog's Knowledge tab lives today.
      if (source === 'product-knowledge-ready' && productId) {
        const knowledgeLink = buildRoute(
          ROUTES.ORG_CATALOG_PRODUCT_KNOWLEDGE,
          { orgId, selectedProduct: ONE_TO_MANY_PRODUCT, productId },
          feedId ? { sfid: feedId } : undefined,
        );
        notificationPresentationDetails.link = knowledgeLink;
        notificationPresentationDetails.shouldShowAction = !window.location?.href?.includes(knowledgeLink);
      }

      const handleNotificationDismiss = () => {
        setMostRecentToastNotification(null);
        markToastAsSeen.mutate({ notificationId: id });
        clearFooterNotification();
      };

      handleFooterNotification({
        autoHide: false,
        title: t(`toastNotifications.${source}-title`),
        message: t(`toastNotifications.${source}-message`, {
          ...(source === 'vod-automatic-moderation-video-flagged' && {
            link: (
              <StyledNotificationLink href="https://bambuser.com/legal/free-trial-terms" target="_blank">
                {t('genericLabels.termsAndConditions')}
              </StyledNotificationLink>
            ),
          }),
        }),
        actionOnClose: () => handleNotificationDismiss(),
        ...(notificationPresentationDetails.shouldShowAction && {
          actionText: t(`toastNotifications.${source}-action-text`),
          action: () => {
            handleNotificationDismiss();
            history.push(notificationPresentationDetails.link);
          },
        }),
      });
    }
  }, [mostRecentToastNotification, handleFooterNotification, clearFooterNotification, markToastAsSeen, t, history]);

  useEffect(() => {
    const allowedToastNotifications = toasts?.filter(({ source }) => KNOWN_TOAST_NOTIFICATION_SOURCES.includes(source)) || [];
    const mostRecentAllowedNotification = allowedToastNotifications[0];
    if (mostRecentAllowedNotification) setMostRecentToastNotification(mostRecentAllowedNotification);
  }, [toasts]);

  useEffect(() => {
    const allowedBannerNotifications = banners?.filter(({ source }) => KNOWN_BANNER_NOTIFICATION_SOURCES.includes(source)) || [];
    const mostRecentAllowedNotification = allowedBannerNotifications[0];
    if (mostRecentAllowedNotification) setMostRecentBannerNotification(mostRecentAllowedNotification);
  }, [banners]);

  const handleBannerNotificationDismiss = useCallback((id) => {
    setMostRecentBannerNotification(null);
    markBannerAsSeen.mutate({ notificationId: id });
  }, [markBannerAsSeen]);

  const context = {
    toasts,
    mostRecentBannerNotification,
    handleBannerNotificationDismiss,
  };

  return <UserNotificationsContext.Provider value={context}>{props.children}</UserNotificationsContext.Provider>;
};

export const useUserNotificationsContext = () => useContext(UserNotificationsContext);
